import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './index.module.scss'
import TaskStatusPicker from "../../../../../Pickers/TaskStatusPicker";
import DatePicker from "../../../../../Pickers/TaskEstimatePicker";
import UsersPicker from "../../../../../Pickers/UsersPicker";
import TaskPriorityPicker from "../../../../../Pickers/TaskPriorityPicker";
import TimeTrackerPicker from "../../../../../Pickers/TimeTrackerPicker";
import TimeLogService from "../../../../../../services/time-log.service";
import DropDown from "../../../../../DropDown";
import InfoTooltip from "../../../../../InfoTooltip";
import EditIcon from "../../../../../../assets/icon/EditIcon";
import { TASK_TYPES, getTaskType } from '../../utils/taskTypes';
import { getProjectTaskStatuses } from '../../../../../../page/Project/utils/taskStatuses';
import { getTaskSpentHours } from '../../../../../../utils/taskSpentTime';

const SHORT_MONTHS_RU = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек']

const formatShortDate = (date: Date) => {
    const d = new Date(date)
    return `${d.getDate()} ${SHORT_MONTHS_RU[d.getMonth()]}`
}

const PlusIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M8 3.33337V12.6667M3.33337 8H12.6667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
)

const ConfirmIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="24" height="24" rx="12" fill="currentColor"/>
        <path d="M16.5488 8.12988C16.6567 8.12988 16.7637 8.15105 16.8633 8.19238C16.9628 8.23369 17.0528 8.29481 17.1289 8.37109C17.2052 8.44721 17.2663 8.53719 17.3076 8.63672C17.3489 8.73632 17.3701 8.84333 17.3701 8.95117C17.3701 9.05899 17.3489 9.16604 17.3076 9.26562C17.2663 9.36514 17.2052 9.45514 17.1289 9.53125L10.8301 15.8291C10.7539 15.9055 10.6641 15.9675 10.5645 16.0088C10.4649 16.0501 10.3578 16.0713 10.25 16.0713C10.1422 16.0713 10.0351 16.0501 9.93555 16.0088C9.86101 15.9778 9.79162 15.9358 9.72949 15.8848L9.66992 15.8301L6.87012 13.0303C6.71643 12.8764 6.62988 12.6677 6.62988 12.4502C6.62994 12.2327 6.71635 12.024 6.87012 11.8701C7.02404 11.7162 7.2335 11.6299 7.45117 11.6299C7.66869 11.63 7.87743 11.7163 8.03125 11.8701L10.25 14.0898L15.9688 8.37109C16.0449 8.29481 16.1349 8.2337 16.2344 8.19238C16.334 8.15106 16.441 8.12989 16.5488 8.12988Z" fill="white" stroke="white" strokeWidth="0.24"/>
    </svg>
)

const CrossIcon = () => (
    <svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d="M0.25143 0.2515C0.586165 -0.0832353 1.12888 -0.0832351 1.46361 0.2515L8.73671 7.5246C9.07145 7.85933 9.07145 8.40205 8.73671 8.73678C8.40198 9.07152 7.85926 9.07152 7.52453 8.73678L0.25143 1.46368C-0.0833055 1.12895 -0.0833053 0.586235 0.25143 0.2515Z" fill="#B1AEC7"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M0.251429 8.73678C-0.0833056 8.40205 -0.0833058 7.85933 0.251429 7.5246L7.52453 0.2515C7.85926 -0.0832353 8.40198 -0.083235 8.73671 0.2515C9.07145 0.586235 9.07145 1.12895 8.73671 1.46368L1.46361 8.73678C1.12888 9.07152 0.586165 9.07152 0.251429 8.73678Z" fill="#B1AEC7"/>
    </svg>
)

interface RelatedTaskProps {
    addRelatedTask: (task: any, changes?: Record<string, any>) => void
    onOpenTask?: (task: any) => void
    onDelete?: () => void
    project: any
    currentRelatedTask: any
}

export default function RelatedTask({ addRelatedTask, onOpenTask, onDelete, project, currentRelatedTask }: RelatedTaskProps){
    const isSaved = !!currentRelatedTask
    const [isExpanded, setIsExpanded] = useState<boolean>(false)
    const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false)
    const [titleDraft, setTitleDraft] = useState<string>('')
    const containerRef = useRef<HTMLDivElement | null>(null)
    const inputRef = useRef<HTMLInputElement | null>(null)
    const titleInputRef = useRef<HTMLInputElement | null>(null)

    const [taskData, setTaskData] = useState<any>({
        title: null,
        status: "backlog",
        priority: null,
        spendTime: getTaskSpentHours(currentRelatedTask),
        timeLogId: currentRelatedTask?.timeLogId ?? currentRelatedTask?.time_log_id ?? null,
        timeLogDescription: currentRelatedTask?.timeLogDescription ?? currentRelatedTask?.time_log_description ?? "",
        description: "",
        due_date: null,
        assignee_ids: [],
        task_type: 'task',
        ...(currentRelatedTask || {})
    });

    useEffect(() => {
        if (!currentRelatedTask) return

        const visibleFields = ['title', 'status', 'priority', 'task_type', 'due_date', 'assignee_ids', 'description']
        const changes = Object.fromEntries(visibleFields
            .filter((field) => currentRelatedTask[field] !== undefined)
            .map((field) => [field, currentRelatedTask[field]]))
        setTaskData((previous: any) => Object.entries(changes).every(([field, value]) => (
            JSON.stringify(previous[field]) === JSON.stringify(value)
        )) ? previous : { ...previous, ...changes })
    }, [currentRelatedTask])

    useEffect(() => {
        let isMounted = true

        const loadTimeLog = async () => {
            if (!currentRelatedTask?.id) {
                return
            }

            try {
                const timeLog = await TimeLogService.getByTaskId(currentRelatedTask.id)

                if (!isMounted) {
                    return
                }

                const minutesSpent = Number(timeLog?.minutes_spent)
                const spendTime = timeLog && !Number.isNaN(minutesSpent)
                    ? minutesSpent / 60
                    : getTaskSpentHours(currentRelatedTask)

                setTaskData((prev: any) => ({
                    ...prev,
                    spendTime,
                    spend_time: spendTime,
                    timeLogId: timeLog?.id ?? null,
                    timeLogDescription: timeLog?.description ?? '',
                }))
            } catch (error) {
                console.error('Failed to load related task time log:', error)
            }
        }

        void loadTimeLog()

        return () => {
            isMounted = false
        }
    }, [currentRelatedTask?.id])

    useEffect(() => {
        if (isSaved || !isExpanded) {
            return
        }

        const handleOutsideClick = (event: MouseEvent) => {
            const target = event.target as HTMLElement | null

            if (!target) {
                return
            }

            if (containerRef.current && containerRef.current.contains(target)) {
                return
            }

            if (target.closest('[data-dropdown-root]')) {
                return
            }

            setIsExpanded(false)
        }

        document.addEventListener('mousedown', handleOutsideClick)

        return () => document.removeEventListener('mousedown', handleOutsideClick)
    }, [isExpanded, isSaved])

    useEffect(() => {
        if (!isSaved && isExpanded) {
            inputRef.current?.focus()
        }
    }, [isExpanded, isSaved])

    const commitIfSaved = (nextTaskData: any, changes: Record<string, any>) => {
        if (isSaved) {
            addRelatedTask(nextTaskData, changes)
        }
    }

    const updateTaskData = (patch: Record<string, any>) => {
        if (Object.entries(patch).every(([key, value]) => JSON.stringify(taskData[key]) === JSON.stringify(value))) return
        const next = { ...taskData, ...patch }
        setTaskData(next)
        commitIfSaved(next, patch)
    }

    const setTitle = (title: string) => setTaskData({ ...taskData, title })
    const setStatus = (statusId: string | number) => updateTaskData({ status: statusId })
    const setPriority = (priorityId: string | number) => updateTaskData({ priority: priorityId })
    const setEstimate = (dueDate: Date) => updateTaskData({ due_date: dueDate })
    const setSpendTime = (spendTime: number) => updateTaskData({ spendTime, spend_time: spendTime })
    const setTaskType = (taskType: string) => updateTaskData({ task_type: taskType })

    const setUsers = (usersId: number | string) => {
        const currentUserIds = Array.isArray(taskData.assignee_ids) ? taskData.assignee_ids : []
        const isSelected = currentUserIds.some((currentUserId: string | number) => String(currentUserId) === String(usersId))
        const newUsersId = isSelected
            ? currentUserIds.filter((currentUserId: string | number) => String(currentUserId) !== String(usersId))
            : [...currentUserIds, usersId]

        updateTaskData({ assignee_ids: newUsersId })
    }

    const createTask = () => {
        if (!taskData.title?.trim()) return
        addRelatedTask(taskData)
    }

    const handleDelete = () => {
        // Clear the in-progress form (local state survives because React reuses
        // this instance by key), then remove the slot from the parent list.
        setTaskData({
            title: null,
            status: "backlog",
            priority: null,
            spendTime: null,
            timeLogId: null,
            timeLogDescription: "",
            description: "",
            due_date: null,
            assignee_ids: [],
            task_type: 'task',
        })
        setIsExpanded(false)
        onDelete?.()
    }

    const startTitleEdit = () => {
        setTitleDraft(taskData.title || '')
        setIsEditingTitle(true)
    }

    const finishTitleEdit = () => {
        const nextTitle = titleDraft.trim()
        setIsEditingTitle(false)

        if (!nextTitle || nextTitle === taskData.title) {
            return
        }

        updateTaskData({ title: nextTitle })
    }

    useEffect(() => {
        if (isEditingTitle) {
            const input = titleInputRef.current
            input?.focus()
            input?.select()
        }
    }, [isEditingTitle])

    const selectedTaskType = getTaskType(taskData.task_type)
    const hasTitle = !!taskData.title?.trim()
    const hasUsers = Array.isArray(taskData.assignee_ids) && taskData.assignee_ids.length > 0
    const hasDueDate = !!taskData.due_date
    const hasPriority = taskData.priority != null && taskData.priority !== ''
    const hasSpendTime = taskData.spendTime != null && taskData.spendTime !== ''
    const projectStatuses = useMemo(() => getProjectTaskStatuses(project), [project])

    // Saved stage — ListView card view
    if (isSaved) {
        return (
            <div className={styles.savedItem}>
                <div className={styles.savedItem__content}>
                    <div className={styles.savedItem__leadIcons}>
                        <InfoTooltip content="Тип задачи">
                        <div className={styles.related__tooltipWrap}>
                            <DropDown
                                closeDropDownByPick
                                position="left"
                                btn={(
                                    <div className={styles.related__typeBtn}>
                                        {selectedTaskType.icon}
                                    </div>
                                )}
                            >
                                <div className={styles.related__typeDropdown}>
                                    {TASK_TYPES.map((type) => (
                                        <button
                                            key={type.id}
                                            type="button"
                                            className={`${styles.related__typeOption} ${taskData.task_type === type.id ? styles.related__typeOptionActive : ''}`}
                                            onClick={() => setTaskType(type.id)}
                                        >
                                            <span className={styles.related__typeOptionIcon}>{type.icon}</span>
                                            <span>{type.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </DropDown>
                        </div>
                        </InfoTooltip>
                        <InfoTooltip content="Статус">
                        <div className={styles.related__tooltipWrap}>
                            <div className={styles.savedItem__statusIcon}>
                                <TaskStatusPicker
                                    taskData={taskData}
                                    getStatus={setStatus}
                                    pickedId={taskData.status}
                                    statuses={projectStatuses}
                                    onlyIcon
                                    dropdownPosition="left"
                                />
                            </div>
                        </div>
                        </InfoTooltip>
                    </div>
                    {isEditingTitle ? (
                        <input
                            ref={titleInputRef}
                            className={styles.savedItem__titleInput}
                            value={titleDraft}
                            onChange={(e) => setTitleDraft(e.target.value)}
                            onBlur={finishTitleEdit}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') finishTitleEdit()
                                if (e.key === 'Escape') setIsEditingTitle(false)
                            }}
                        />
                    ) : (
                        <div className={styles.savedItem__titleWrap}>
                            {taskData.id && onOpenTask ? (
                                <button
                                    type="button"
                                    className={styles.savedItem__title}
                                    onClick={() => onOpenTask(taskData)}
                                    onDoubleClick={startTitleEdit}
                                >
                                    {taskData.title}
                                </button>
                            ) : (
                                <span
                                    className={styles.savedItem__title}
                                    onDoubleClick={startTitleEdit}
                                >
                                    {taskData.title}
                                </span>
                            )}
                            <button
                                type="button"
                                className={styles.savedItem__renameBtn}
                                onClick={startTitleEdit}
                                aria-label="Переименовать этап"
                            >
                                <EditIcon />
                            </button>
                        </div>
                    )}
                </div>
                <div className={styles.savedItem__col}>
                    <UsersPicker
                        getUsers={setUsers}
                        usersId={taskData.assignee_ids}
                        projectId={project?.id}
                        dropdownPosition="right"
                    />
                </div>
                <div className={styles.savedItem__col}>
                    <DatePicker
                        setEstimate={setEstimate}
                        estimate={taskData.due_date}
                        dropdownPosition="right"
                        formatDate={formatShortDate}
                    />
                </div>
                <div className={styles.savedItem__col}>
                    <TaskPriorityPicker
                        getPriority={setPriority}
                        priorityId={taskData.priority}
                        dropdownPosition="right"
                    />
                </div>
                <div className={styles.savedItem__col}>
                    <TimeTrackerPicker
                        getSpendTime={setSpendTime}
                        spendTime={taskData.spendTime}
                        dropdownPosition="right"
                    />
                </div>
            </div>
        )
    }

    // Add-form collapsed
    if (!isExpanded) {
        return (
            <div
                className={`${styles.related} ${styles.related_collapsed}`}
                onClick={() => setIsExpanded(true)}
            >
                <div className={styles.related__addIcon}>
                    <PlusIcon />
                </div>
                <div className={styles.related__placeholder}>Добавить этап</div>
            </div>
        )
    }

    // Add-form expanded
    return (
        <div className={styles.related} ref={containerRef}>
            <div className={styles.related__line}>
                <div className={styles.related__leadIcon}>
                    <InfoTooltip content="Тип задачи">
                    <div className={styles.related__tooltipWrap}>
                        <DropDown
                            closeDropDownByPick
                            position="left"
                            btn={(
                                <div className={styles.related__typeBtn}>
                                    {selectedTaskType.icon}
                                </div>
                            )}
                        >
                            <div className={styles.related__typeDropdown}>
                                {TASK_TYPES.map((type) => (
                                    <button
                                        key={type.id}
                                        type="button"
                                        className={`${styles.related__typeOption} ${taskData.task_type === type.id ? styles.related__typeOptionActive : ''}`}
                                        onClick={() => setTaskType(type.id)}
                                    >
                                        <span className={styles.related__typeOptionIcon}>{type.icon}</span>
                                        <span>{type.label}</span>
                                    </button>
                                ))}
                            </div>
                        </DropDown>
                    </div>
                    </InfoTooltip>
                </div>

                <div className={styles.related__leadIcon}>
                    <InfoTooltip content="Статус">
                    <div className={styles.related__tooltipWrap}>
                        <TaskStatusPicker
                            taskData={taskData}
                            getStatus={setStatus}
                            pickedId={taskData.status}
                            statuses={projectStatuses}
                            onlyIcon
                            dropdownPosition="left"
                        />
                    </div>
                    </InfoTooltip>
                </div>

                <div className={styles.related__inputWrap}>
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder="Добавить этап"
                        value={taskData.title || ''}
                        onChange={(e) => setTitle(e.target.value)}
                    />
                </div>

                <div className={styles.related__icons}>
                    <div className={hasUsers ? styles.related__field : styles.related__icon}>
                        <UsersPicker getUsers={setUsers} usersId={taskData.assignee_ids} onlyIcon={!hasUsers} projectId={project?.id} dropdownPosition="right"/>
                    </div>
                    <div className={hasDueDate ? styles.related__field : styles.related__icon}>
                        <DatePicker setEstimate={setEstimate} estimate={taskData.due_date} onlyIcon={!hasDueDate} dropdownPosition="right"/>
                    </div>
                    <div className={hasPriority ? styles.related__field : styles.related__icon}>
                        <TaskPriorityPicker getPriority={setPriority} priorityId={taskData.priority} onlyIcon={!hasPriority} dropdownPosition="right"/>
                    </div>
                    <div className={hasSpendTime ? styles.related__field : styles.related__icon}>
                        <TimeTrackerPicker getSpendTime={setSpendTime} spendTime={taskData.spendTime} onlyIcon={!hasSpendTime} dropdownPosition="right"/>
                    </div>
                </div>

                <div className={styles.related__actions}>
                    {onDelete && (
                        <button
                            type="button"
                            className={styles.related__deleteBtn}
                            onClick={handleDelete}
                            aria-label="Удалить этап"
                        >
                            <CrossIcon />
                        </button>
                    )}
                    <button
                        type="button"
                        className={`${styles.related__confirmBtn} ${!hasTitle ? styles.related__confirmBtnDisabled : ''}`}
                        onClick={createTask}
                        disabled={!hasTitle}
                    >
                        <ConfirmIcon />
                    </button>
                </div>
            </div>
        </div>
    )
}
